import React, { useEffect, useState } from 'react';
import { Tasks } from '../../FinancialPlanOPL/Goals/components/Tasks/Tasks';
import { LogevityAnalysis } from '../../FinancialPlanOPL/Goals/goals/Retirement/tabs/LongevityAnalysis/LongevityAnalysis';
import { SocialSecurity } from '../../FinancialPlanOPL/Goals/goals/Retirement/tabs/SocialSecurity/SocialSecurity';
import { useAreBothPartiesCurrentlyReceiving } from '../../FinancialPlanOPL/Goals/goals/Retirement/tabs/SocialSecurity/useAreBothPartiesCurrentlyReceiving';
import {
  removeQueryLongevityAnalysis,
  removeQueryMarketScenarios,
  removeQuerySocialSecurity,
} from '../../FinancialPlanOPL/Goals/helpers/legacyWhatIfsFinancialPlanHelpers';
import { MonteCarlo } from '../../MonteCarlo/MonteCarlo';
import RetirementAssetsChart from '../../RetirementAssetsChart/RetirementAssetsChartContainer';
import RetirementIncomeChart from '../../RetirementIncomeChart/RetirementIncomeChartContainer';
import ActionStepsV2 from '../ActionStepsV2/ActionStepsV2Container';
import AssumptionsSectionView from '../Components/AssumptionsTable';
import Explanations from '../Explanations/ExplanationsContainer';
import FinancialPlanHeader from '../FinancialPlanHeaderView';
import Footer from '../Footer/FooterContainer';
import GeneralAssumptionsView from '../GeneralAssumptions';
import { RothConversionOutput } from './components/RothConversionOutput';
import retirementAssumptionsLabelMap from './RetirementAssumptionsConst';
import {
  ASSUMPTIONS,
  LONGEVITY_ANALYSIS,
  MONTE_CARLO,
  OVERVIEW,
  RETIREMENT_INCOME,
  ROTH_CONVERSION,
  SOCIAL_SECURITY,
} from './RetirementConstants';

const RetirementView = props => {
  const {
    clientId,
    data,
    fpId,
    isCurrentPlan,
    isPrint,
    proposedPlanName,
    selectedGoalChart,
    selectedSection,
    showCalibration,
    hasChart,
    retirementAssumptions,
    retirementGoal,
    goalId,
  } = props;

  const isSocialSecuritySelected = selectedSection === SOCIAL_SECURITY;
  const hideSocialSecurity = useAreBothPartiesCurrentlyReceiving();
  const [hasOpenedSocialSecurity, setHasOpenedSocialSecurity] = useState(isSocialSecuritySelected);

  useEffect(() => {
    if (isSocialSecuritySelected) {
      setHasOpenedSocialSecurity(true);
    }
  }, [isSocialSecuritySelected]);

  useEffect(() => {
    return () => {
      removeQueryLongevityAnalysis({ clientId, fpId });
      removeQuerySocialSecurity({ clientId, fpId });
      removeQueryMarketScenarios({ clientId, fpId });
    };
  }, [clientId, fpId]);

  if (!retirementGoal) {
    return null;
  }

  const rothConversionData = retirementGoal.GoalCharts.find(
    ({ Title }) => Title === ROTH_CONVERSION,
  )?.Data;

  return (
    <div className="NewFinancialPlan_retirement" style={{ position: 'relative' }}>
      <div
        className={`
          NewFinancialPlan_header
          ${!showCalibration ? ' hide-calibration-button' : ''}
        `}
      >
        <FinancialPlanHeader
          {...props}
          hideOldDescription={false}
          hideTrackIndicator={selectedSection === ASSUMPTIONS && isPrint}
        />
      </div>
      <section className="calibration" />

      {/* @ts-ignore */}
      {!isPrint && <Explanations clientId={clientId} />}

      {selectedSection === LONGEVITY_ANALYSIS && (
        <section>
          <LogevityAnalysis />
        </section>
      )}

      {/* Keep the tab mounted after the first visit so comparison/PoS are not refetched.
          Hide with visibility instead of display:none — Highcharts pies measure 0×0
          when an ancestor is display:none and stay broken when the tab is shown again. */}
      {!hideSocialSecurity &&
        (isPrint ? isSocialSecuritySelected : isSocialSecuritySelected || hasOpenedSocialSecurity) && (
        <section
          aria-hidden={!isSocialSecuritySelected}
          style={
            isSocialSecuritySelected
              ? undefined
              : {
                  position: 'absolute',
                  width: '100%',
                  visibility: 'hidden',
                  pointerEvents: 'none',
                  overflow: 'hidden',
                  height: 0,
                }
          }
        >
          <SocialSecurity />
        </section>
      )}

      {selectedSection === OVERVIEW && hasChart && (
        <section className="chart no-padding">
          <RetirementAssetsChart
            isPrint={isPrint}
            clientId={clientId}
            proposedPlanName={proposedPlanName}
            data={selectedGoalChart}
            isCurrentPlan={isCurrentPlan}
            fpId={fpId}
          />
        </section>
      )}
      {selectedSection === MONTE_CARLO && (
        <MonteCarlo clientId={clientId} fpId={fpId} isPrint={isPrint} />
      )}
      {selectedSection === RETIREMENT_INCOME && (
        <section className="chart no-padding">
          <RetirementIncomeChart
            clientId={clientId}
            fpId={fpId}
            isPrint={isPrint}
            goal={retirementGoal}
          />
        </section>
      )}
      {selectedSection === ROTH_CONVERSION && (
        <RothConversionOutput clientId={clientId} goalId={goalId} data={rothConversionData} />
      )}
      {selectedSection === ASSUMPTIONS && !isPrint && (
        <div className="assumptions-container">
          <AssumptionsSectionView
            title="Retirement Goal Assumptions"
            valuesList={retirementAssumptions}
            labelList={retirementAssumptionsLabelMap}
          />
          <br />

          {/* @ts-ignore */}
          <GeneralAssumptionsView clientId={clientId} />
        </div>
      )}

      {!isPrint && selectedSection === OVERVIEW && <Tasks />}

      {isPrint && selectedSection === OVERVIEW && (
        <ActionStepsV2 clientId={clientId} fpId={fpId} goalId={retirementGoal.GoalId} />
      )}

      {!isPrint && <Footer data={data} clientId={clientId} fpId={fpId} />}
    </div>
  );
};

export default RetirementView;
